import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { combineLatest, BehaviorSubject } from 'rxjs';
import moment from 'moment';
import { DataTableColumn } from 'src/app/shared/components';
import {
  TechChangeAgentDashboardListDto,
  TechChangeCardSummaryDetail,
  TechChangeCombinedSummaryResponseDto,
  TechChangeMonthlyExportSummaryResponseDto,
  TechChangeMonthlySummaryResponseDto,
  WeeklySummaryItem,
} from 'src/app/shared/dto';
import { GrowthSignalsDataLabelsEnum } from '../../enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type TechChangeSummary =
  | TechChangeCombinedSummaryResponseDto
  | TechChangeMonthlySummaryResponseDto
  | TechChangeMonthlyExportSummaryResponseDto;

@UntilDestroy()
@Component({
  selector: 'app-tech-change-card',
  templateUrl: './tech-change-card.component.html',
  styleUrls: ['../../common-reporting-dashboard.scss'],
  standalone: false,
})
export class TechChangeCardComponent implements OnInit {
  @Input() isShowChart: boolean;
  @Input() set selectedAgent(value: TechChangeAgentDashboardListDto | null) {
    this._selectedAgent = value;
    this.selectedAgentSubject.next(value);
  }
  get selectedAgent(): TechChangeAgentDashboardListDto | null {
    return this._selectedAgent;
  }

  @Input() set summary(value: TechChangeSummary) {
    this.summarySubject.next(value);
  }

  @Output() runAgent = new EventEmitter<void>();
  @Output() setWeeklySummary = new EventEmitter<TechChangeCardSummaryDetail>();

  private _selectedAgent: TechChangeAgentDashboardListDto | null = null;
  isWeeklyAgentsNotRun: boolean = false;
  isAllAgentsEmailOnly: boolean = false;
  isAllAgentsExportOnly: boolean = false;
  chartLabels: string[] = [];
  chartData: Record<string, number[]> = {};
  barChartData: number[] = [];
  tableData: WeeklySummaryItem[];
  tableColumns: DataTableColumn[] = [];
  disabledSeries: string[] = [];

  readonly growthSignalsDataLabelsEnum: typeof GrowthSignalsDataLabelsEnum = GrowthSignalsDataLabelsEnum;

  private selectedAgentSubject = new BehaviorSubject<TechChangeAgentDashboardListDto | null>(null);
  private summarySubject = new BehaviorSubject<TechChangeSummary | null>(null);

  private readonly tableColumnBase: DataTableColumn[] = [
    {
      dataField: 'displayDate',
      caption: 'Date',
      cellTemplate: 'dateTemplate',
      width: '40%',
      cellClasses: 'border-b-0 pb-1 px-4',
      headerCellClasses: 'py-1',
      dateFormat: 'MM/dd/yy',
    },
    {
      dataField: 'total_company_count',
      caption: GrowthSignalsDataLabelsEnum.COMPANIES_EXPORTED,
      cellTemplate: 'numberTemplate',
      width: '20%',
      allowSorting: false,
      cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
      headerCellClasses: 'py-1 text-center',
    },
    {
      dataField: 'total_contact_count',
      caption: GrowthSignalsDataLabelsEnum.CONTACTS_EXPORTED,
      cellTemplate: 'numberTemplate',
      width: '20%',
      allowSorting: false,
      cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
      headerCellClasses: 'py-1 text-center',
    },
  ];

  private readonly allCompaniesColumn: DataTableColumn = {
    dataField: 'all_companies_count',
    caption: GrowthSignalsDataLabelsEnum.ALL_COMPANIES,
    cellTemplate: 'numberTemplate',
    width: '20%',
    allowSorting: false,
    cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
    headerCellClasses: 'py-1 text-center',
  };

  ngOnInit(): void {
    combineLatest([this.selectedAgentSubject, this.summarySubject])
      .pipe(untilDestroyed(this))
      .subscribe(([agent, summary]) => {
        if (summary) {
          this.processSummaryData(summary, agent === null, agent);
        }
      });
  }

  private processSummaryData(
    res: TechChangeSummary,
    isCombined: boolean,
    agent: TechChangeAgentDashboardListDto | null,
  ): void {
    const isCombinedRes = this.isCombinedResponse(res);

    const isEmailOnly = isCombinedRes ? res.is_email_only : false;
    const isExportOnly = isCombinedRes ? res.is_export_only : false;

    this.isAllAgentsEmailOnly = agent?.email_only || isEmailOnly || false;
    this.isAllAgentsExportOnly = isExportOnly || (!agent?.email_only && agent !== null) || false;

    if (agent?.email_only || isEmailOnly) {
      this.processEmailOnlyData(res, isCombined);
    } else if (isExportOnly || (!agent?.email_only && agent !== null)) {
      this.processExportData(res);
    } else {
      this.processNormalData(res, isCombined);
    }
  }

  private processNormalData(res: TechChangeSummary, isCombined: boolean): void {
    this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => {
      const base = (entry.total_company_count || 0) + (entry.total_contact_count || 0);
      return sum + (isCombined ? base + (entry.all_companies_count || 0) : base);
    }, 0) === 0;

    this.tableData = res.weekly_summary.map((data: WeeklySummaryItem) => ({
      ...data,
      displayDate: moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY'),
      total_company_count: data.total_company_count || 0,
      total_contact_count: data.total_contact_count || 0,
      ...(isCombined && { all_companies_count: data.all_companies_count || 0 }),
    }));

    this.chartLabels = [];
    this.chartData = {
      [GrowthSignalsDataLabelsEnum.COMPANIES_EXPORTED]: [],
      [GrowthSignalsDataLabelsEnum.CONTACTS_EXPORTED]: [],
      ...(isCombined && { [GrowthSignalsDataLabelsEnum.ALL_COMPANIES]: [] }),
    };

    res.weekly_summary.forEach((data: WeeklySummaryItem) => {
      const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY');
      this.chartLabels.push(weekLabel);
      this.chartData[GrowthSignalsDataLabelsEnum.COMPANIES_EXPORTED].push(data.total_company_count || 0);
      this.chartData[GrowthSignalsDataLabelsEnum.CONTACTS_EXPORTED].push(data.total_contact_count || 0);
      if (isCombined) {
        this.chartData[GrowthSignalsDataLabelsEnum.ALL_COMPANIES].push(data.all_companies_count || 0);
      }
    });

    this.disabledSeries = isCombined ? [GrowthSignalsDataLabelsEnum.ALL_COMPANIES] : [];

    this.tableColumns = isCombined
      ? [...this.tableColumnBase, this.allCompaniesColumn]
      : this.tableColumnBase;
  }

  private processExportData(res: TechChangeMonthlyExportSummaryResponseDto): void {
    this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + (entry.total_company_count || 0) + (entry.total_contact_count || 0), 0) === 0;

    this.setWeeklySummary.emit(res.tech_card_summary_detail);

    this.tableData = res.weekly_summary.map((data: WeeklySummaryItem) => ({
      ...data,
      displayDate: moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY'),
      total_company_count: data.total_company_count || 0,
      total_contact_count: data.total_contact_count || 0,
    }));

    this.chartLabels = [];
    this.chartData = {
      [GrowthSignalsDataLabelsEnum.COMPANIES]: [],
      [GrowthSignalsDataLabelsEnum.CONTACTS]: [],
    };

    res.weekly_summary.forEach((data: WeeklySummaryItem) => {
      const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY');
      this.chartLabels.push(weekLabel);
      this.chartData[GrowthSignalsDataLabelsEnum.COMPANIES].push(data.total_company_count || 0);
      this.chartData[GrowthSignalsDataLabelsEnum.CONTACTS].push(data.total_contact_count || 0);
    });

    this.tableColumns = [
      this.tableColumnBase[0],
      {
        dataField: 'total_company_count',
        caption: GrowthSignalsDataLabelsEnum.COMPANIES,
        cellTemplate: 'numberTemplate',
        width: '20%',
        allowSorting: false,
        cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
        headerCellClasses: 'py-1 text-center',
      },
      {
        dataField: 'total_contact_count',
        caption: GrowthSignalsDataLabelsEnum.CONTACTS,
        cellTemplate: 'numberTemplate',
        width: '20%',
        allowSorting: false,
        cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
        headerCellClasses: 'py-1 text-center',
      },
    ];
  }

  private processEmailOnlyData(res: TechChangeMonthlySummaryResponseDto, isCombined: boolean): void {
    const countField = isCombined ? 'all_companies_count' : 'total_company_count';
    this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + (entry[countField] || 0), 0) === 0;

    this.setWeeklySummary.emit(res.tech_card_summary_detail);

    this.tableData = res.weekly_summary.map((data: WeeklySummaryItem) => ({
      ...data,
      displayDate: moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY'),
      total_company_count: data[countField] || 0,
    }));

    this.chartLabels = [];
    this.barChartData = [];
    this.chartData = {};

    res.weekly_summary.forEach((data: WeeklySummaryItem) => {
      const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY');
      this.chartLabels.push(weekLabel);
      this.barChartData.push(data[countField] || 0);
    });

    this.tableColumns = [
      this.tableColumnBase[0],
      {
        dataField: 'total_company_count',
        caption: GrowthSignalsDataLabelsEnum.ALL_COMPANIES,
        cellTemplate: 'numberTemplate',
        width: '40%',
        allowSorting: false,
        cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
        headerCellClasses: 'py-1 text-center',
      },
    ];
  }

  private isCombinedResponse(res: TechChangeSummary): res is TechChangeCombinedSummaryResponseDto {
    return 'is_email_only' in res && 'is_export_only' in res;
  }
}
